import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessageSquareHeart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { bewertungAPI } from '@/lib/api';

/* v8 M20: Bewertungs-Automation. Nach positiven Momenten (Gürtel, Monat 1, Turniererfolg, Ende Ernährungsabo/Coaching-Paket)
   entsteht ein ENTWURF in der Freigabe-Zentrale — gesendet wird nie von selbst. Ein Link, ein Text für alle. */

const ANLASS = { graduierung: 'Gürtel', onboarding_tag30: 'Monat 1', turnier_erfolg: 'Turnier', ernaehrung_abo_ende: 'Ernährung', pt_paket_ende: 'Coaching' };
const fehlerText = (e) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Speichern fehlgeschlagen');

function Zahl({ label, wert }) {
  return (
    <div className="rounded-lg border border-border/60 p-3">
      <div className="text-xl font-semibold tabular-nums">{wert ?? 0}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

export default function BewertungAutomatikKarte() {
  const [stand, setStand] = useState(null);
  const [aktiv, setAktiv] = useState(false);
  const [link, setLink] = useState('');
  const [stats, setStats] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(() => {
    bewertungAPI.einstellungen().then((r) => { setStand(r.data); setAktiv(!!r.data.aktiv); setLink(r.data.link || ''); }).catch(() => setStand({}));
    bewertungAPI.stats(90).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const darf = !!stand?.darf_aendern;
  const speichern = async (neuAktiv = aktiv) => {
    setLaeuft(true);
    try { await bewertungAPI.setzen({ aktiv: neuAktiv, link }); setAktiv(neuAktiv); toast.success('Gespeichert'); laden(); }
    catch (e) { toast.error(fehlerText(e)); }
    finally { setLaeuft(false); }
  };

  return (
    <Card className="border-border/60 mt-4" data-testid="bewertung-automatik-karte">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><MessageSquareHeart className="w-4 h-4" /> Google-Bewertungen anfragen</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Nach einem Gürtel, dem ersten Monat, einem Turniererfolg oder dem Ende eines Ernährungs- oder Coaching-Pakets legt das CRM einen
          Entwurf in der Freigabe-Zentrale an. Gesendet wird nie von selbst. Pro Person höchstens alle 180 Tage, nicht bei offener
          Rücklastschrift oder laufender Kündigung.{!darf && ' Ändern kann nur die Leitung.'}
        </p>
        <div className="flex items-center gap-3">
          <Switch checked={aktiv} onCheckedChange={(v) => speichern(v)} disabled={!darf || laeuft} data-testid="bewertung-schalter" />
          <span className="text-sm">{aktiv ? 'An' : 'Aus'}</span>
        </div>
        <div className="max-w-xl">
          <Label className="text-xs">Link zur Google-Bewertung (https)</Label>
          <div className="flex gap-2 mt-1">
            <Input value={link} onChange={(e) => setLink(e.target.value)} disabled={!darf || laeuft} placeholder="https://g.page/r/…/review" data-testid="bewertung-link" />
            {darf && <Button size="sm" className="bg-marke text-white hover:bg-marke/90" onClick={() => speichern()} disabled={laeuft}>Speichern</Button>}
          </div>
        </div>
        {stats && (
          <div>
            <div className="text-xs text-muted-foreground mb-2">Letzte {stats.tage} Tage</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Zahl label="Entwürfe" wert={stats.anfragen} />
              <Zahl label="Gesendet" wert={stats.gesendet} />
              <Zahl label="Geklickt" wert={stats.geklickt} />
              <Zahl label="Klicks gesamt" wert={stats.klicks} />
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground space-y-0.5">
              {Object.entries(stats.je_kanal || {}).map(([k, v]) => <div key={k}>{k === 'whatsapp' ? 'WhatsApp' : 'E-Mail'}: {v.anfragen} Entwürfe, {v.gesendet} gesendet, {v.klicks} Klicks</div>)}
              {Object.entries(stats.je_anlass || {}).map(([k, v]) => <div key={k}>{ANLASS[k] || k}: {v.anfragen} Entwürfe, {v.gesendet} gesendet, {v.klicks} Klicks</div>)}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
